import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import MapboxMap, { Diagnose } from 'react-mapbox-wrapper';

import Throbber from 'components/Throbber';
import { isFunction } from 'helpers/ObjectUtils';
import SentryWrapper from 'helpers/Sentry';
import { translate } from 'helpers/Translate';

/**
 * BasicMap Component.
 */
export default class BasicMap extends Component {
    /**
     * Creates an instance of BasicMap.
     * @param {Object} props Component props
     */
    constructor(props) {
        super(props);

        /**
         * Viewport is stored in state for better handling for debounced zoomEnd behavior
         */
        this.state = {
            hasError: false,
            viewport: {
                coordinates: this.props.coordinates,
                zoom: this.props.zoom,
            },
        };

        /**
         * Store Mapbox map instance
         * @type {mapboxgl.Map}
         */
        this.map = null;

        this.onLoad = this.onLoad.bind(this);
        this.onViewportChange = this.onViewportChange.bind(this);
        this.fitBounds = this.fitBounds.bind(this);
    }

    /**
     * React lifecycle.
     * @param {Object} prevProps Next props
     */
    componentDidUpdate(prevProps) {
        let update = false;
        const viewport = { ...this.state.viewport };

        const currentCenter = this.props.coordinates;
        const prevCenter = prevProps.coordinates;
        const currentZoom = this.props.zoom;
        const prevZoom = prevProps.zoom;

        if (currentCenter.lat !== prevCenter.lat || currentCenter.lng !== prevCenter.lng) {
            viewport.coordinates = currentCenter;
            update = true;
        }

        if (currentZoom !== prevZoom) {
            viewport.zoom = currentZoom;
            update = true;
        }

        if (update) {
            // eslint-disable-next-line react/no-did-update-set-state
            this.setState({ viewport });
        }
    }

    /**
     * React lifecycle.
     * @param  {Error} error Error thrown
     * @param  {Object} info Informations
     */
    componentDidCatch(error, info) {
        this.setState({ hasError: true });
        this.diagnose(error, info);
    }

    /**
     * onMap library load.
     */
    // eslint-disable-next-line no-undef
    onLoad(map) {
        const { onLoad } = this.props;

        this.map = map;
        // eslint-disable-next-line no-param-reassign
        map.fitBounds = this.fitBounds;

        if (isFunction(onLoad)) {
            onLoad(map);
        }
    }

    /**
     * Update Map property when viewport change.
     * @param  {Object} viewport New viewport
     */
    // eslint-disable-next-line no-undef
    onViewportChange(viewport) {
        this.setState({ viewport });

        const { onViewportChange } = this.props;
        if (isFunction(onViewportChange)) {
            onViewportChange(viewport);
        }
    }

    /**
     * Fit bounding box to given bounds
     * @param  {Object} bounds Bounding box
     */
    // eslint-disable-next-line no-undef
    fitBounds(bounds) {
        this.map.jumpTo(this.map.cameraForBounds(bounds, { padding: 20 }));
    }

    /**
     * Render props for displaying incompatible mapbox
     */
    // eslint-disable-next-line no-undef
    fallback() {
        return (
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    background:
                        'repeating-linear-gradient(45deg, aliceblue, aliceblue 10px, white 10px, white 20px)',
                }}
            >
                <div
                    style={{
                        backgroundColor: 'white',
                        padding: 10,
                        borderRadius: 4,
                        fontWeight: 'bold',
                        fontSize: 24,
                    }}
                >
                    {translate(
                        "Votre navigateur n'est pas compatible avec notre outil de cartographie",
                    )}
                </div>
            </div>
        );
    }

    /**
     * Diagnose error on mapboxgl.
     */
    // eslint-disable-next-line no-undef
    diagnose(error, info) {
        try {
            Diagnose.fullDiagnostic().then((output) => {
                global.console.log(output, error, info);

                if (output.supported) {
                    if (SentryWrapper.isInitialized) {
                        if (error) {
                            SentryWrapper.Sentry.captureException(error);
                        }

                        SentryWrapper.Sentry.captureMessage(output);
                    }
                }
            });
        } catch (e) {
            global.console.error('Error while running Mapbox Diagnostic', e);
        }
    }

    /**
     * React lifecycle.
     */
    render() {
        const {
            children,
            className,
            fetching,
            mapStyle,
            maxZoom,
            minZoom,
            onClick,
            onLayerChange,
            onZoomEnd,
            onZoomStart,
            style,
            withFullscreen,
            withInteraction,
            withCompass,
            withZoom,
        } = this.props;

        return (
            <div className={classNames(className, { 'panel--fetching': fetching })} style={style}>
                {this.state.hasError && this.fallback()}
                {!this.state.hasError && (
                    <MapboxMap
                        minZoom={minZoom}
                        maxZoom={maxZoom}
                        accessToken={MA.Config.MAPBOX_ACCESS_TOKEN}
                        className="mapbox-map"
                        interactive={withInteraction}
                        onChange={this.onViewportChange}
                        onLoad={this.onLoad}
                        onClick={onClick}
                        onLayerChange={onLayerChange}
                        onZoomStart={onZoomStart}
                        onZoomEnd={onZoomEnd}
                        withFullscreen={withFullscreen}
                        withCompass={withCompass}
                        withZoom={withZoom}
                        style={mapStyle}
                        renderNotSupported={null}
                        {...this.state.viewport}
                    >
                        {children}
                    </MapboxMap>
                )}
                {fetching && <Throbber />}
            </div>
        );
    }
}

BasicMap.propTypes = {
    className: PropTypes.string,
    coordinates: PropTypes.shape({
        lat: PropTypes.number.isRequired,
        lng: PropTypes.number.isRequired,
    }).isRequired,
    children: PropTypes.oneOfType([PropTypes.node, PropTypes.arrayOf(PropTypes.node)]),
    fetching: PropTypes.bool,
    mapStyle: PropTypes.string,
    maxZoom: PropTypes.number,
    minZoom: PropTypes.number,
    onClick: PropTypes.func,
    onLayerChange: PropTypes.func,
    onLoad: PropTypes.func,
    onViewportChange: PropTypes.func,
    onZoomEnd: PropTypes.func,
    onZoomStart: PropTypes.func,
    style: PropTypes.shape({}),
    withCompass: PropTypes.bool,
    withFullscreen: PropTypes.bool,
    withInteraction: PropTypes.bool,
    withZoom: PropTypes.bool,
    zoom: PropTypes.number,
};

BasicMap.defaultProps = {
    mapStyle: MA.Config.MAPBOX_STYLES,
    withInteraction: true,
    zoom: 15,
};
